import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { carregarPainelPresencial } from '@/lib/presencial';
import AbaPessoas from './AbaPessoas';
import AbaRelatorios from '../components/AbaRelatorios';
import MontagemEquipes from '../direcao/components/MontagemEquipes';
import Presencas from '../direcao/components/Presencas';
import PresencialCampistas from './PresencialCampistas';
import HubCampistas from './HubCampistas';
import type { TelaCampista } from './HubCampistas';
import HubEquipe from './HubEquipe';
import type { TelaEquipe } from './HubEquipe';
import TelaVagasCampistas from './TelaVagasCampistas';
import TelaTitulo from './TelaTitulo';
import DeslizarEntrada from '@/components/movimento/DeslizarEntrada';
import Dica from '@/components/feature/Dica';
import type { SituacaoCamiseta } from '../components/RelatorioCamisetas';
import TribosCampistas from './tribos/TribosCampistas';
import type { GrupoVagas } from '../types';
import type { FormacaoPresenca } from '../direcao/types';

type PapelPublico = 'equipe' | 'campista';
type TelaPublico = TelaCampista | TelaEquipe;

interface Props {
  eventoId: number;
  eventoNome: string;
  eventoCapa?: string | null;
  eventoLocal?: string | null;
  usuarioId: string;
  eventoInicio: string;
  mostrarFicha: boolean;
  papel: PapelPublico;
  subInicial?: string;
}

const ROTULO: Record<PapelPublico, string> = {
  equipe: 'Equipe',
  campista: 'Campistas',
};

const CARD = 'rounded-3xl bg-white border border-background-200 p-4';

// Telas que cada aba abre a partir do hub (?aba=equipe|campistas&tela=...).
const TELAS: Record<PapelPublico, TelaPublico[]> = {
  equipe: ['pessoas', 'montagem', 'formacoes', 'relatorios'],
  campista: ['pessoas', 'vagas', 'tribos', 'presencial', 'relatorios'],
};

const TITULO_TELA: Record<TelaPublico, string> = {
  pessoas: 'Pessoas',
  vagas: 'Vagas e suplentes',
  tribos: 'Tribos',
  presencial: 'Inscrição presencial',
  montagem: 'Montagem das equipes',
  formacoes: 'Formações e presenças',
  relatorios: 'Relatórios',
};

// Aba "Equipe" e aba "Campistas" do acampamento: cada público com o seu hub,
// e cada cartão abre a função numa tela cheia (?tela=...).
export default function AbaPublicoEvento({
  eventoId,
  eventoNome,
  eventoCapa,
  eventoLocal,
  usuarioId,
  eventoInicio,
  mostrarFicha,
  papel,
  subInicial = 'pessoas',
}: Props) {
  void subInicial;
  const [searchParams, setSearchParams] = useSearchParams();
  const [idsDoPapel, setIdsDoPapel] = useState<number[]>([]);
  const [modalidadesDoPapel, setModalidadesDoPapel] = useState<number[]>([]);
  const [contagens, setContagens] = useState({ comVaga: 0, suplentes: 0, grupos: 0 });
  const [vagasHub, setVagasHub] = useState({ grupos: 0, livresAlerta: 0 });
  const [presencial, setPresencial] = useState({ tem: false, aguardando: 0 });
  const [equipeHub, setEquipeHub] = useState<{ semEquipe: number; proxima: string | null }>({
    semEquipe: 0,
    proxima: null,
  });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<{ ids: number[]; resumo: string } | null>(null);

  // Todas as inscrições deste público — base dos relatórios e dos números do hub.
  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const [resMods, resInscritos] = await Promise.all([
          supabase.from('modalidades').select('id, papel').eq('evento_id', eventoId),
          supabase
            .from('vw_inscritos')
            .select('inscricao_id, modalidade_id, status, grupo')
            .eq('evento_id', eventoId),
        ]);
        if (resMods.error) throw resMods.error;
        if (resInscritos.error) throw resInscritos.error;
        if (!ativo) return;

        const idsModalidades = new Set(
          ((resMods.data as { id: number; papel: string | null }[]) || [])
            .filter((m) => m.papel === papel)
            .map((m) => Number(m.id)),
        );
        const linhas =
          (resInscritos.data as {
            inscricao_id: number;
            modalidade_id: number;
            status: string;
            grupo: string | null;
          }[]) || [];
        const ids: number[] = [];
        let comVaga = 0;
        let suplentes = 0;
        const grupos = new Set<string>();
        linhas.forEach((i) => {
          if (!idsModalidades.has(Number(i.modalidade_id))) return;
          if (i.status === 'cancelada') return;
          if (i.status === 'lista_espera') {
            // Suplente não entra nas contas: só aparece na descrição.
            suplentes += 1;
          } else {
            ids.push(Number(i.inscricao_id));
            comVaga += 1;
          }
          if (i.grupo) grupos.add(i.grupo);
        });
        setIdsDoPapel(ids);
        setModalidadesDoPapel([...idsModalidades]);
        setContagens({ comVaga, suplentes, grupos: grupos.size });
        setFiltro(null);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e, 'Não foi possível carregar esta aba.'));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId, papel]);

  // Números do hub: vagas/presença (campistas) ou sem equipe/próxima formação (equipe).
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        if (papel === 'campista') {
          const [resPainel, painelPresencial] = await Promise.all([
            supabase.rpc('painel_vagas', { p_evento: eventoId }),
            carregarPainelPresencial(eventoId).catch(() => null),
          ]);
          if (!ativo) return;
          const grupos =
            !resPainel.error && Array.isArray(resPainel.data)
              ? (resPainel.data as GrupoVagas[])
              : [];
          const comVagas = grupos.filter((g) => g.vagas !== null && g.vagas !== undefined);
          const alertados = comVagas.filter((g) => g.alerta_vaga_livre);
          setVagasHub({
            grupos: comVagas.length,
            livresAlerta: alertados.reduce((s, g) => s + Number(g.livres ?? 0), 0),
          });
          const mods = painelPresencial?.modalidades ?? [];
          setPresencial({
            tem: mods.length > 0,
            aguardando: mods.reduce((s, m) => s + m.aguardando, 0),
          });
          return;
        }

        const [resSem, resPresencas] = await Promise.all([
          supabase.from('vw_sem_equipe').select('participante_id').eq('evento_id', eventoId),
          supabase.rpc('resumo_presencas', { p_evento: eventoId }),
        ]);
        if (!ativo) return;
        const semEquipe =
          !resSem.error && Array.isArray(resSem.data) ? resSem.data.length : 0;
        const formacoes =
          !resPresencas.error && Array.isArray(resPresencas.data)
            ? (resPresencas.data as FormacaoPresenca[])
            : [];
        const agora = Date.now();
        const proximas = formacoes
          .filter((f) => !f.cancelada && new Date(f.quando).getTime() >= agora)
          .sort((a, b) => new Date(a.quando).getTime() - new Date(b.quando).getTime());
        setEquipeHub({ semEquipe, proxima: proximas[0]?.quando ?? null });
      } catch {
        /* sem números extras: os cartões continuam abrindo */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId, papel]);

  // ?tela= é o endereço novo; ?sub= mantém vivos os links antigos do painel.
  const telaDaUrl = searchParams.get('tela') ?? searchParams.get('sub');
  const tela: TelaPublico | null =
    telaDaUrl && TELAS[papel].includes(telaDaUrl as TelaPublico)
      ? (telaDaUrl as TelaPublico)
      : null;

  function abrirTela(destino: TelaPublico) {
    const next = new URLSearchParams(searchParams);
    next.set('aba', papel === 'campista' ? 'campistas' : 'equipe_acampamento');
    next.set('tela', destino);
    next.delete('sub');
    // Entrar pelo cartão abre a lista do jeito normal (sem filtro herdado).
    next.delete('situacao');
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function voltarAoHub() {
    const next = new URLSearchParams(searchParams);
    next.delete('tela');
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const rotulo = ROTULO[papel];
  const inscricaoIds = filtro ? filtro.ids : idsDoPapel;
  const resumoFiltro = filtro
    ? filtro.resumo
    : `${rotulo} · ${contagens.comVaga} com vaga${
        contagens.suplentes > 0 ? ` · ${contagens.suplentes} suplentes` : ''
      }`;

  // A lista da aba já define a situação (o relatório obedece exatamente o que está na tela).
  const resumoBaixo = (filtro?.resumo ?? '').toLowerCase();
  const situacaoCamisetas: SituacaoCamiseta = !filtro
    ? 'com_vaga'
    : resumoBaixo.includes('suplente')
      ? 'suplentes'
      : resumoBaixo.includes('cancelad')
        ? 'todos'
        : 'com_vaga';
  const rotuloSituacaoCamisetas = resumoBaixo.includes('suplente')
    ? 'Suplentes'
    : resumoBaixo.includes('cancelad')
      ? 'Canceladas'
      : 'Com vaga';

  const relatorios = (
    <div className={CARD}>
      <h3 className="text-[15px] font-bold text-foreground-950">
        {papel === 'equipe' ? 'Relatórios da equipe' : 'Relatórios e gráficos dos campistas'}
      </h3>
      <p className="mt-0.5 text-[13px] text-foreground-600">Filtro: {resumoFiltro}</p>
      <div className="mt-4">
        <AbaRelatorios
          eventoId={eventoId}
          eventoNome={eventoNome}
          inscricaoIds={inscricaoIds}
          resumoFiltro={resumoFiltro}
          papel={papel}
          situacaoCamisetas={situacaoCamisetas}
          travarSituacaoCamisetas={!!filtro}
          rotuloSituacaoCamisetas={rotuloSituacaoCamisetas}
          camisetaIds={filtro ? filtro.ids : null}
        />
      </div>
    </div>
  );

  const listaPessoas = (
    <AbaPessoas
      eventoId={eventoId}
      eventoNome={eventoNome}
      eventoCapa={eventoCapa}
      eventoLocal={eventoLocal}
      vendeIngressos={false}
      mostrarFicha={mostrarFicha}
      rotuloParticipacoes={papel === 'equipe' ? 'inscrições da equipe' : 'campistas'}
      rotuloPublico={rotulo}
      usuarioId={usuarioId}
      eventoInicio={eventoInicio}
      papel={papel}
      aoMudarFiltro={setFiltro}
      ocultarPainelVagas
      aoAbrirVagas={papel === 'campista' ? () => abrirTela('vagas') : undefined}
    />
  );

  const carregandoBox = (
    <div className="space-y-3">
      <div className="h-11 esqueleto rounded-full bg-secondary-100" />
      <div className="h-64 esqueleto rounded-3xl bg-secondary-100" />
    </div>
  );

  const erroBox = (
    <div className={`${CARD} p-10 text-center`}>
      <p className="text-[15px] text-foreground-800">{erro}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
      >
        Tentar de novo
      </button>
    </div>
  );

  if (carregando) return carregandoBox;
  if (erro) return erroBox;

  if (!tela) {
    return (
      <DeslizarEntrada className="space-y-3">
        <div className="flex items-center gap-2 px-1">
          <h2 className="min-w-0 truncate text-[17px] font-bold text-foreground-950">{rotulo}</h2>
          <Dica
            id={papel === 'campista' ? 'painel.pessoas' : 'painel.equipes'}
            eventoId={eventoId}
          />
        </div>
        {papel === 'campista' ? (
          <HubCampistas
            comVaga={contagens.comVaga}
            suplentes={contagens.suplentes}
            grupos={vagasHub.grupos || contagens.grupos}
            vagasLivresAlerta={vagasHub.livresAlerta}
            temPresencial={presencial.tem}
            aguardandoPresencial={presencial.aguardando}
            aoAbrir={abrirTela}
          />
        ) : (
          <HubEquipe
            comVaga={contagens.comVaga}
            suplentes={contagens.suplentes}
            semEquipe={equipeHub.semEquipe}
            proximaFormacao={equipeHub.proxima}
            aoAbrir={abrirTela}
          />
        )}
      </DeslizarEntrada>
    );
  }

  const titulo =
    tela === 'relatorios'
      ? papel === 'equipe'
        ? 'Relatórios da equipe'
        : 'Relatórios dos campistas'
      : TITULO_TELA[tela];

  const DICA_DA_TELA: Record<TelaPublico, string> = {
    pessoas: 'painel.pessoas',
    vagas: 'painel.pessoas',
    tribos: 'painel.pessoas',
    presencial: 'painel.pessoas',
    montagem: 'painel.equipes',
    formacoes: 'painel.encontros',
    relatorios: papel === 'equipe' ? 'painel.equipes' : 'painel.pessoas',
  };

  return (
    <DeslizarEntrada>
      <TelaTitulo
        titulo={titulo}
        aoVoltar={voltarAoHub}
        dicaId={DICA_DA_TELA[tela]}
        eventoId={eventoId}
      />

      {tela === 'pessoas' && listaPessoas}
      {tela === 'vagas' && (
        <TelaVagasCampistas
          eventoId={eventoId}
          modalidades={modalidadesDoPapel}
          aoAbrirPessoa={() => abrirTela('pessoas')}
        />
      )}
      {tela === 'tribos' && <TribosCampistas eventoId={eventoId} />}
      {tela === 'presencial' && <PresencialCampistas eventoId={eventoId} />}
      {tela === 'montagem' && <MontagemEquipes eventoId={eventoId} />}
      {tela === 'formacoes' && <Presencas eventoId={eventoId} />}
      {tela === 'relatorios' && relatorios}
    </DeslizarEntrada>
  );
}